/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useRef, TouchEvent } from 'react';
import { 
  Phone, 
  MessageCircle, 
  CheckCircle2, 
  MapPin, 
  Star, 
  ArrowRight, 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X,
  Hammer,
  Layers,
  Bath,
  Grid,
  Layout,
  Droplets,
  Mail
} from 'lucide-react';
import { motion, AnimatePresence, useScroll, useTransform } from 'motion/react';

const PHONE_NUMBER = "+1 (843) 957-4321";
const EMAIL_ADDRESS = "Workhouseofmb@gmail.com";
const WHATSAPP_LINK = "https://wa.link/sld2ik";
const PHONE_LINK = "tel:+18439574321";
const MAILTO_LINK = `mailto:${EMAIL_ADDRESS}`;
const GOOGLE_MAPS_LINK = "https://maps.google.com/maps?q=8217%20Essence%20Dr,%20Longs,%20SC%2029568&t=&z=15&ie=UTF8&iwloc=&output=embed";
const BUSINESS_LISTING_LINK = "https://maps.app.goo.gl/4LLM2yvhSeRweZDx9";

const SERVICES = [
  {
    title: "Tile Installation",
    description: "Precision-laid tiles for stunning walls and floors that stand the test of time.",
    icon: <Grid size={32} />
  },
  {
    title: "Flooring",
    description: "Expert installation of luxury materials to elevate the foundation of your home.",
    icon: <Layers size={32} />
  },
  {
    title: "Shower Installation",
    description: "Custom shower designs with waterproof precision and elegant glass finishes.",
    icon: <Droplets size={32} />
  },
  {
    title: "Backsplash",
    description: "The perfect kitchen accent combining aesthetic appeal with easy-to-clean durability.",
    icon: <Layout size={32} />
  },
  {
    title: "Luxury Vinyl Plank (LVP)",
    description: "Durable, waterproof, and beautiful flooring that mimics real wood without the maintenance.",
    icon: <Hammer size={32} />
  },
  {
    title: "Bathroom Remodeling",
    description: "Full-service transformation of your most private space into a luxury sanctuary.",
    icon: <Bath size={32} />
  }
];

const GALLERY_IMAGES = [
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909465/71199563238__83385AB5-F172-412F-B1C6-14307DF3E081_sp7inf.jpg" },
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909465/71685707537__8558D14F-4C90-4CB9-BE4F-9AC39117E943_iseclr.jpg" },
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909465/IMG_8902_s1otur.jpg" },
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909464/IMG_6960_okddmp.jpg" },
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909464/70604286927__8B55531B-0C5E-46B4-BB00-D22BC973B494_guajnf.jpg" },
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909464/IMG_6851_exxzyb.jpg" },
  { url: "https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777909464/IMG_5313_qsjmuz.jpg" },
];

const TESTIMONIALS = [
  { name: "Sarah Johnson", rating: 5, text: "The attention to detail in our new bathroom is incredible. The tile work is absolutely flawless." },
  { name: "Michael Chen", rating: 5, text: "Professional, on-time, and delivered exactly what was promised. Our kitchen backsplash looks amazing." },
  { name: "Emily Rodriguez", rating: 5, text: "We love our new LVP floors! The team was efficient and left everything spotless. Highly recommend." },
];

const Logo = ({ className = "", noBlend = false }: { className?: string; noBlend?: boolean }) => (
  <div className={className}>
    <img 
      src="https://res.cloudinary.com/dnhcqwnpl/image/upload/v1777675748/work_house_3_cuy3dn.png" 
      alt="Work House Logo" 
      className={`h-full w-auto object-contain ${noBlend ? '' : 'mix-blend-multiply'}`}
      referrerPolicy="no-referrer"
    />
  </div>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Logo className="h-16 md:h-20" />
        </div>

        <nav className="hidden md:flex items-center gap-8">
          {['Services', 'Work', 'Reviews', 'Contact'].map((item) => (
            <a 
              key={item} 
              href={`#${item.toLowerCase()}`}
              className={`text-sm font-semibold uppercase tracking-widest hover:text-brand-accent transition-colors ${
                isScrolled ? 'text-brand-primary' : 'text-white'
              }`}
            >
              {item}
            </a>
          ))}
          <a 
            href={WHATSAPP_LINK} 
            className="bg-brand-accent text-white px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform"
          >
            Get Quote
          </a>
        </nav>

        <button 
          className="md:hidden text-white" 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={28} className="text-brand-primary" /> : <Menu size={28} className={isScrolled ? 'text-brand-primary' : 'text-white'} />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-0 left-0 w-full h-screen bg-white flex flex-col items-center justify-center gap-8"
          >
            {['Services', 'Work', 'Reviews', 'Contact'].map((item) => (
              <a 
                key={item} 
                href={`#${item.toLowerCase()}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-2xl font-bold uppercase tracking-widest text-brand-primary"
              >
                {item}
              </a>
            ))}
            <a 
              href={WHATSAPP_LINK}
              className="bg-brand-accent text-white px-10 py-4 rounded-full text-lg font-bold uppercase tracking-widest"
            >
              Get Free Quote
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Carousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Minimum swipe distance for a move to be registered
  const minSwipeDistance = 50;

  const onTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };

  useEffect(() => {
    if (!isHovered) {
      timerRef.current = setInterval(nextSlide, 4000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isHovered]);

  return (
    <div 
      className="relative w-full max-w-6xl mx-auto overflow-hidden rounded-3xl shadow-2xl"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {GALLERY_IMAGES.map((img, idx) => (
          <div key={idx} className="w-full shrink-0 relative group aspect-[16/9]">
            <img 
              src={img.url} 
              alt={`Work House Project ${idx + 1}`} 
              className={`w-full h-full object-cover transition-transform duration-[4000ms] ease-linear ${idx === currentIndex ? 'scale-110' : 'scale-100'}`}
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-40" />
          </div>
        ))}
      </div>

      <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 transition-colors">
        <ChevronLeft size={24} />
      </button>
      <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 transition-colors">
        <ChevronRight size={24} />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {GALLERY_IMAGES.map((_, idx) => (
          <button 
            key={idx} 
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-8' : 'bg-white/50'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default function App() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[95vh] flex items-center justify-center overflow-hidden bg-brand-primary">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1920&h=1080&auto=format&fit=crop" 
            alt="Luxury Home Remodeling" 
            className="w-full h-full object-cover opacity-40"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center text-white max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-8xl font-extrabold uppercase tracking-tight leading-[0.9] mb-6">
              Premium Tile & <br />
              <span className="text-brand-accent">Remodeling</span> Done Right
            </h1>
            <p className="text-lg md:text-xl font-light opacity-90 mb-10 max-w-2xl mx-auto leading-relaxed">
              We transform bathrooms and living spaces with precision, quality materials, 
              and expert craftsmanship you can trust.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href={WHATSAPP_LINK}
                className="w-full sm:w-auto bg-brand-accent text-white px-10 py-5 rounded-full flex items-center justify-center gap-3 font-bold uppercase tracking-widest hover:scale-105 transition-transform"
              >
                <MessageCircle size={20} />
                WhatsApp Quote
              </a>
              <a 
                href={PHONE_LINK}
                className="w-full sm:w-auto bg-white/10 backdrop-blur-md border border-white/20 text-white px-10 py-5 rounded-full flex items-center justify-center gap-3 font-bold uppercase tracking-widest hover:bg-white/20 transition-all"
              >
                <Phone size={20} />
                Call Now
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] font-medium opacity-60">
              Fast response • No obligation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-white border-y border-gray-100 py-10">
        <div className="container mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            "Licensed & Insured",
            "100% Satisfaction",
            "Free Estimations",
            "On-Time Service"
          ].map((text, idx) => (
            <div key={idx} className="flex flex-col items-center justify-center text-center gap-2">
              <CheckCircle2 className="text-brand-accent" size={24} />
              <span className="text-xs font-bold uppercase tracking-widest text-brand-primary">{text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brand-accent mb-4">Our Expertise</h2>
            <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-brand-primary">
              High-End Remodeling Services
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {SERVICES.map((s, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -10 }}
                className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="text-brand-accent mb-6">{s.icon}</div>
                <h4 className="text-xl font-bold uppercase tracking-tight mb-4">{s.title}</h4>
                <p className="text-gray-500 font-light leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Carousel Section */}
      <section id="work" className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-2xl">
              <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brand-accent mb-4">Visual Proof</h2>
              <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-brand-primary">Our Work Speaks For Itself</h3>
              <p className="text-gray-500 mt-6 font-light">Explore some of our recent projects. Every detail reflects our commitment to quality, precision, and high-end finishes.</p>
            </div>
            <div className="hidden md:block">
              <a href={WHATSAPP_LINK} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:text-brand-accent transition-colors">
                View More <ArrowRight size={16} />
              </a>
            </div>
          </div>
          
          <Carousel />

          <div className="mt-16 text-center max-w-2xl mx-auto">
            <p className="text-brand-primary font-medium italic mb-10">
              "Every project is built with precision, premium materials, and attention to detail."
            </p>
            <div className="bg-gray-50 p-10 rounded-3xl inline-block w-full">
              <p className="text-xl font-bold uppercase mb-4">Like what you see?</p>
              <a 
                href={WHATSAPP_LINK}
                className="bg-brand-accent text-white px-10 py-5 rounded-full inline-flex items-center gap-3 font-bold uppercase tracking-widest hover:scale-105 transition-transform"
              >
                <MessageCircle size={20} />
                Chat with us
              </a>
              <p className="text-xs uppercase tracking-widest font-medium opacity-60 mt-4">
                Tell us about your project — we’ll guide you step by step.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="rounded-3xl overflow-hidden shadow-2xl h-[500px] border border-gray-100">
              <iframe 
                src={GOOGLE_MAPS_LINK} 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen={true} 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brand-accent mb-4">Find Us Locally</h2>
              <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-brand-primary mb-8">
                Expert Craftsmanship In Your Area
              </h3>
              <p className="text-gray-500 font-light text-lg mb-10 leading-relaxed">
                We are proud to serve our local community. Visit our Google Business Profile to see our location and read reviews from satisfied homeowners.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-accent/10 p-3 rounded-full text-brand-accent">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold uppercase tracking-tight">Main Office</h5>
                    <p className="text-gray-500 font-light">8217 Essence Dr, Longs, SC 29568 (Serving Myrtle Beach Area)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-brand-accent/10 p-3 rounded-full text-brand-accent">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold uppercase tracking-tight">Email Us</h5>
                    <p className="text-gray-500 font-light truncate max-w-[200px] md:max-w-none">
                      <a href={MAILTO_LINK} className="hover:text-brand-accent transition-colors">{EMAIL_ADDRESS}</a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-brand-accent/10 p-3 rounded-full text-brand-accent">
                    <Star size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold uppercase tracking-tight">Google Rating</h5>
                    <p className="text-gray-500 font-light">5.0 Star Rating from over 50+ local reviews</p>
                  </div>
                </div>
              </div>
              <a 
                href={BUSINESS_LISTING_LINK}
                target="_blank"
                rel="noreferrer"
                className="mt-12 inline-flex items-center gap-2 border-b-2 border-brand-accent pb-2 text-sm font-bold uppercase tracking-widest hover:text-brand-accent transition-colors"
              >
                View Us on Google Maps <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="reviews" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brand-accent mb-4">Client Feedback</h2>
            <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-brand-primary">Trusted By Homeowners</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} className="bg-white p-10 rounded-2xl border border-gray-100">
                <div className="flex gap-1 text-brand-accent mb-6">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-gray-600 font-light leading-relaxed mb-8 italic">"{t.text}"</p>
                <p className="font-bold uppercase tracking-widest text-sm text-brand-primary">— {t.name}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="flex flex-col items-center gap-6">
              <p className="text-lg font-bold uppercase tracking-widest">Have a project in mind?</p>
              <a 
                href={WHATSAPP_LINK}
                className="bg-brand-accent text-white px-10 py-5 rounded-full inline-flex items-center gap-3 font-bold uppercase tracking-widest hover:scale-105 transition-transform"
              >
                <MessageCircle size={20} />
                Send us a Message
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=800&h=1000&auto=format&fit=crop" 
                alt="High-End Home Remodeling" 
                className="rounded-3xl shadow-2xl relative z-10 object-cover w-full h-[600px] block"
                referrerPolicy="no-referrer"
              />
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-brand-accent rounded-3xl -z-0 opacity-20" />
            </div>
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brand-accent mb-4">The Premium Choice</h2>
              <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-brand-primary mb-10">Why Choose Us</h3>
              
              <ul className="space-y-6">
                {[
                  { title: "Attention to detail", desc: "No corner cut, no tile misplaced. Perfection is our standard." },
                  { title: "Premium materials", desc: "We only work with top-tier vendors ensuring longevity." },
                  { title: "Experienced team", desc: "Over 15 years of specialty remodeling experience." },
                  { title: "Clean and efficient work", desc: "A tidy workspace is a happy homeowner." },
                  { title: "Satisfaction guarantee", desc: "We don’t leave until you are absolutely thrilled." }
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-4">
                    <CheckCircle2 className="text-brand-accent shrink-0 mt-1" size={20} />
                    <div>
                      <h5 className="font-bold uppercase tracking-tight text-sm mb-1">{item.title}</h5>
                      <p className="text-gray-500 text-sm font-light">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-12 pt-12 border-t border-gray-100">
                <p className="text-2xl font-light italic text-brand-primary mb-8 leading-snug">
                  "We don’t just remodel spaces — we upgrade your lifestyle."
                </p>
                <a 
                  href={WHATSAPP_LINK}
                  className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-brand-accent hover:gap-4 transition-all"
                >
                  Chat with us today and get started <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-brand-primary py-24 text-center">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-white mb-6">
              Ready to <span className="text-brand-accent">Transform</span> Your Space?
            </h2>
            <p className="text-white/70 text-lg md:text-xl font-light mb-12 max-w-2xl mx-auto">
              Get expert advice, fast answers, and a free quote — all in one message. 
              Our team is ready to bring your vision to life.
            </p>
            <a 
              href={WHATSAPP_LINK}
              className="bg-brand-accent text-white px-12 py-6 rounded-full inline-flex items-center gap-3 font-extrabold uppercase tracking-widest hover:scale-105 transition-transform shadow-xl"
            >
              <MessageCircle size={24} />
              Chat on WhatsApp
            </a>
            <p className="mt-6 text-xs uppercase tracking-widest font-medium text-white/40">
              Quick response • No pressure
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-16 text-white border-t border-white/5">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 items-start">
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="flex items-center mb-6">
              <Logo className="h-20 md:h-24" noBlend={true} />
            </div>
            <p className="text-white/50 font-light max-w-xs text-sm leading-relaxed">
              Dedicated to delivering the highest quality home improvements with integrity and precision. 
              Quality work. Honest service. Beautiful results.
            </p>
          </div>

          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <h5 className="text-xs font-bold uppercase tracking-[0.2em] mb-6 text-brand-accent">Contact</h5>
            <ul className="space-y-4 text-sm font-medium">
              <li>
                <a href={PHONE_LINK} className="flex items-center justify-center md:justify-start gap-3 text-white/70 hover:text-white transition-colors">
                  <Phone size={16} /> {PHONE_NUMBER}
                </a>
              </li>
              <li>
                <a href={MAILTO_LINK} className="flex items-center justify-center md:justify-start gap-3 text-white/70 hover:text-white transition-colors text-ellipsis overflow-hidden">
                  <Mail size={16} className="shrink-0" /> {EMAIL_ADDRESS}
                </a>
              </li>
              <li>
                <a href={WHATSAPP_LINK} className="flex items-center justify-center md:justify-start gap-3 text-white/70 hover:text-white transition-colors">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </li>
            </ul>
          </div>

          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <h5 className="text-xs font-bold uppercase tracking-[0.2em] mb-6 text-brand-accent">Service Areas</h5>
            <ul className="space-y-3 text-sm font-medium text-white/70">
              <li>Myrtle Beach</li>
              <li>North Myrtle Beach</li>
              <li>Conway</li>
              <li>Little River</li>
              <li>Longs</li>
            </ul>
          </div>
        </div>
        
        <div className="container mx-auto px-6 mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-center text-[10px] text-white/30 uppercase tracking-widest">
          <div>
            &copy; {new Date().getFullYear()} Work House. All rights reserved.
          </div>
          <div className="hover:text-white transition-colors">
            Made by <a href="https://www.yestoweb.com" target="_blank" rel="noopener noreferrer" className="font-bold text-brand-accent hover:underline">Yes to web</a>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a 
        href={WHATSAPP_LINK}
        className="fixed bottom-8 right-8 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-2xl flex items-center gap-3 hover:scale-110 transition-transform group"
      >
        <span className="max-w-0 overflow-hidden group-hover:max-w-[200px] transition-all duration-500 whitespace-nowrap text-sm font-bold uppercase tracking-widest l">
          Chat with us now
        </span>
        <MessageCircle size={28} />
      </a>
    </div>
  );
}
